import { WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsCreateCustomGroupsEnabled } from '@modules/integrationsV2/hooks';
import {
  GetIntegrationsFormDataV2Query,
  IntegrationChannelType,
  IntegrationFormDataViewFragment,
  IntegrationType,
  useGetIntegrationsFormDataV2Query,
} from 'generated/graphql';
import { getIsCohortIntegration } from 'modules/ui/Settings/Integrations/utils/getIsCohortIntegration';
import { useMemo } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading, QueryStatus } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

const isIntegrationChannelTypeEnabled = (
  channelType: IntegrationChannelType,
  integrationType: IntegrationType,
  isCreateCustomgroupIntegratiomEnabled: boolean,
): boolean => {
  switch (channelType) {
    case IntegrationChannelType.Reddit:
    case IntegrationChannelType.ZendeskChat:
    case IntegrationChannelType.Delighted:
    case IntegrationChannelType.Webhook:
    case IntegrationChannelType.Email:
    case IntegrationChannelType.ZendeskSupport:
    case IntegrationChannelType.StellaConnect:
    case IntegrationChannelType.Intercom:
    case IntegrationChannelType.Slack:
    case IntegrationChannelType.Kustomer:
    case IntegrationChannelType.Discourse:
    case IntegrationChannelType.Livechat:
    case IntegrationChannelType.Freshchat:
    case IntegrationChannelType.Playstore:
    case IntegrationChannelType.Appstore:
    case IntegrationChannelType.Twitter:
    case IntegrationChannelType.SurveyMonkey:
    case IntegrationChannelType.ZohoDesk:
    case IntegrationChannelType.Amplitude:
    case IntegrationChannelType.MixPanel:
    case IntegrationChannelType.Front:
    case IntegrationChannelType.G2:
    case IntegrationChannelType.FreshDesk:
    case IntegrationChannelType.Typeform:
    case IntegrationChannelType.Gong:
    case IntegrationChannelType.InApp:
    case IntegrationChannelType.Discord:
    case IntegrationChannelType.Snowflake:
    case IntegrationChannelType.Grain:
    case IntegrationChannelType.FacebookPages:
    case IntegrationChannelType.Salesforce:
    case IntegrationChannelType.Qualtrics:
    case IntegrationChannelType.AmazonConnect:
    case IntegrationChannelType.Chorus:
    case IntegrationChannelType.GoogleMyBusiness:
    case IntegrationChannelType.Gainsight:
    case IntegrationChannelType.Sprig:
      return true;
    case IntegrationChannelType.FileUpload:
      return integrationType === IntegrationType.Cohorts
        ? isCreateCustomgroupIntegratiomEnabled
        : true;
    default:
      return false;
  }
};

interface UseGetIntegrationsFormDataOptions {
  fetchPolicy?: WatchQueryFetchPolicy;
  onCompleted?: (data: GetIntegrationsFormDataV2Query) => void;
}

const useGetIntegrationsFormData = ({
  fetchPolicy = 'cache-and-network',
  onCompleted,
}: UseGetIntegrationsFormDataOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const { data, loading, error, called } = useGetIntegrationsFormDataV2Query({
    variables: { tenantID },
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to fetch integrations list',
        props: {},
      });
    },
    fetchPolicy,
    nextFetchPolicy: 'cache-first',
    onCompleted,
  });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  const isCreateCustomgroupIntegratiomEnabled =
    useIsCreateCustomGroupsEnabled();

  const enabledIntegrationsFormData = useMemo(():
    | IntegrationFormDataViewFragment[]
    | undefined => {
    if (
      status !== QueryStatus.SUCCESS_COMPLETED ||
      !data?.getIntegrationsFormDataV2?.forms
    ) {
      return;
    }

    return data.getIntegrationsFormDataV2.forms
      .filter((form) =>
        isIntegrationChannelTypeEnabled(
          form.channel.type,
          form.integrationType,
          isCreateCustomgroupIntegratiomEnabled,
        ),
      )
      .map((form) => {
        if (getIsCohortIntegration(form.channel.type)) {
          return {
            ...form,
            integrationType: IntegrationType.Cohorts, // TODO: Remove this when we get integration type for cohorts
          };
        }
        return form;
      });
  }, [
    data?.getIntegrationsFormDataV2?.forms,
    isCreateCustomgroupIntegratiomEnabled,
    status,
  ]);

  return {
    data: enabledIntegrationsFormData,
    status,
    error,
    isLoading: IsLoading(status),
  };
};

export default useGetIntegrationsFormData;
